import React, { useState, useEffect, useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ReferenceArea
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';
import { apiRequest } from '../lib/utils';

const PARAM_CONFIG = {
    ph: { label: 'pH', color: '#0ea5e9', unit: '' },
    chlorine: { label: 'Chlore', color: '#10b981', unit: 'ppm' },
    alkalinity: { label: 'TAC', color: '#f59e0b', unit: 'ppm' },
    stabilizer: { label: 'Stabilisant', color: '#8b5cf6', unit: 'ppm' }
};

const PERIODS = [
    { label: '30 j', value: 30 },
    { label: '90 j', value: 90 },
    { label: '1 an', value: 365 }
];

function TrendIcon({ trend }) {
    if (trend === 'up') return <TrendingUp className="h-3.5 w-3.5 text-amber-500" />;
    if (trend === 'down') return <TrendingDown className="h-3.5 w-3.5 text-sky-500" />;
    return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
}

export default function AnalysisHistoryChart({ compact = false }) {
    const [days, setDays] = useState(30);
    const [activeParam, setActiveParam] = useState('ph');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        apiRequest(`/analysis/trends?days=${days}`)
            .then((d) => { if (mounted) setData(d); })
            .catch(() => { if (mounted) setData(null); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [days]);

    const chartData = useMemo(() => data?.data || [], [data]);
    const summary = data?.summary || {};
    const config = PARAM_CONFIG[activeParam];
    const range = data?.ranges?.[activeParam];

    const yDomain = useMemo(() => {
        if (!range) return ['auto', 'auto'];
        const values = chartData.map((p) => p[activeParam]).filter((v) => v != null);
        const min = Math.min(range.warning_min ?? range.ideal_min ?? 0, ...values);
        const max = Math.max(range.warning_max ?? range.ideal_max ?? 10, ...values);
        return [Math.max(0, min - (max - min) * 0.1), max + (max - min) * 0.1];
    }, [range, chartData, activeParam]);

    if (loading) {
        return (
            <Card className="glass rounded-3xl" data-testid="analysis-history-chart-loading">
                <CardContent className="py-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    if (!data || chartData.length === 0) {
        return (
            <Card className="glass rounded-3xl" data-testid="analysis-history-chart-empty">
                <CardContent className="py-10 text-center">
                    <Activity className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                        Pas encore assez de données pour afficher un graphique. Faites quelques analyses pour voir l&apos;évolution de votre eau.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="glass rounded-3xl" data-testid="analysis-history-chart">
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-primary" />
                        Évolution de votre eau
                    </CardTitle>
                    <CardDescription>
                        {data.total_analyses} analyse{data.total_analyses > 1 ? 's' : ''} sur les {days} derniers jours
                    </CardDescription>
                </div>
                <div className="flex gap-1 flex-wrap">
                    {PERIODS.map((p) => (
                        <Button
                            key={p.value}
                            variant={days === p.value ? 'default' : 'outline'}
                            size="sm"
                            className="rounded-full h-8 px-3 text-xs"
                            onClick={() => setDays(p.value)}
                            data-testid={`period-${p.value}`}
                        >
                            {p.label}
                        </Button>
                    ))}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Parameter selector / summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.keys(PARAM_CONFIG).map((key) => {
                        const s = summary[key];
                        const cfg = PARAM_CONFIG[key];
                        const isActive = activeParam === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setActiveParam(key)}
                                data-testid={`param-${key}`}
                                className={`text-left p-3 rounded-xl border transition ${isActive ? 'border-primary bg-primary/10' : 'border-border bg-muted/30 hover:bg-muted/50'}`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium" style={{ color: cfg.color }}>{cfg.label}</span>
                                    {s && <TrendIcon trend={s.trend} />}
                                </div>
                                <p className="text-lg font-bold font-mono">
                                    {s?.latest ?? '-'}
                                    {s && <span className="text-xs font-normal ml-1 text-muted-foreground">{cfg.unit}</span>}
                                </p>
                                {s && <p className="text-[10px] text-muted-foreground">moy. {s.avg}</p>}
                            </button>
                        );
                    })}
                </div>

                {/* Main chart */}
                <div className="h-64 sm:h-72 w-full" data-testid={`chart-${activeParam}`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} domain={yDomain} />
                            <Tooltip
                                contentStyle={{ borderRadius: '12px', border: '1px solid var(--border)' }}
                                formatter={(value) => [`${value} ${config.unit}`.trim(), config.label]}
                            />
                            {/* Ideal zone band */}
                            {range?.ideal_min != null && range?.ideal_max != null && (
                                <ReferenceArea
                                    y1={range.ideal_min}
                                    y2={range.ideal_max}
                                    fill={config.color}
                                    fillOpacity={0.12}
                                    stroke={config.color}
                                    strokeOpacity={0.25}
                                    strokeDasharray="3 3"
                                    ifOverflow="extendDomain"
                                />
                            )}
                            <Line
                                type="monotone"
                                dataKey={activeParam}
                                stroke={config.color}
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: config.color }}
                                activeDot={{ r: 6 }}
                                connectNulls
                            />
                            {!compact && <Legend wrapperStyle={{ fontSize: 12 }} />}
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Legend of ideal zone */}
                {range?.ideal_min != null && (
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <span
                            className="inline-block w-3 h-3 rounded"
                            style={{ background: config.color, opacity: 0.3 }}
                        />
                        Zone idéale : {range.ideal_min} - {range.ideal_max} {config.unit}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
